import { useState } from 'react';
import { ApiError, Avatar, Button, ErrorNote, Sheet, Stars, api, brl, cx, haptic } from '@mobi/web';
import type { PaymentMethod } from '@mobi/shared';
import type { DriverRide } from '../useDriver.js';

/**
 * Fim da corrida, do lado do motorista.
 *
 * O número grande é o que ele tem que receber em dinheiro — já líquido do
 * saldo que o passageiro tinha na carteira. Mostrar a tarifa cheia aqui
 * faria o motorista cobrar a mais.
 */
/** Rótulo do motorista, não do sistema: é o que ele fala no dia a dia. */
const PAYMENTS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'pix', label: 'Pix' },
  { value: 'credit', label: 'Crédito' },
  { value: 'debit', label: 'Débito' },
];

export function FinishedRide({ ride, onDone }: { ride: DriverRide; onDone: () => void }) {
  const [stars, setStars] = useState(0);
  const [payment, setPayment] = useState<PaymentMethod | null>(ride.paymentMethod);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cash = ride.cashDueCents ?? ride.finalFareCents ?? ride.estimate.fareCents;
  // Corrida quitada pela carteira não teve recebimento: não há o que
  // perguntar, e o servidor recusaria o registro.
  const askPayment = cash > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      // O pagamento vai primeiro: é registro do dinheiro. Avaliação é
      // opcional e não pode fazer o registro se perder.
      if (askPayment && payment) {
        await api(`/api/rides/${ride.id}/payment`, { body: { method: payment } });
      }
      if (stars > 0) await api(`/api/rides/${ride.id}/rate`, { body: { stars } });
      haptic();
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) onDone();
      else setError(err instanceof ApiError ? err.message : 'não foi possível avaliar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col justify-end bg-surface-2">
      <Sheet className="max-h-full overflow-y-auto">
        <p className="text-sm font-medium text-muted">Corrida concluída</p>

        <p className="text-xs font-semibold uppercase tracking-wide text-brand">
          receber em dinheiro
        </p>
        <p className="display mt-1 text-5xl">{brl(cash)}</p>

        {ride.walletAppliedCents > 0 && (
          <div className="mt-4 rounded-2xl bg-warn/10 px-4 py-3 text-sm text-warn">
            Atenção: a tarifa foi {brl(ride.finalFareCents ?? ride.estimate.fareCents)}, mas{' '}
            {brl(ride.walletAppliedCents)} foram abatidos do saldo do passageiro.
            <strong className="block"> Cobre apenas {brl(cash)}.</strong>
          </div>
        )}

        {askPayment && (
          <div className="mt-6">
            <p className="font-semibold">Como você recebeu?</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {PAYMENTS.map((option) => {
                const on = payment === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      setPayment(option.value);
                      haptic();
                    }}
                    className={cx(
                      'min-h-12 touch-manipulation rounded-2xl border text-base font-semibold transition',
                      on ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8">
          {ride.passenger && (
            <div className="mb-4 flex items-center gap-4">
              <Avatar
                name={ride.passenger.name}
                src={ride.passenger.hasPhoto ? `/api/rides/${ride.id}/photo/passenger` : null}
                size="lg"
              />
              <div className="min-w-0">
                <p className="truncate text-lg font-bold">{ride.passenger.name}</p>
                <Stars value={ride.passenger.rating} count={ride.passenger.ratingCount} />
              </div>
            </div>
          )}

          <p className="font-semibold">
            Como foi o passageiro{ride.passenger ? ` ${ride.passenger.name.split(' ')[0]}` : ''}?
          </p>
          <div className="mt-3 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
                onClick={() => {
                  setStars(n);
                  haptic();
                }}
                className="p-1"
              >
                <svg
                  viewBox="0 0 20 20"
                  className={cx('h-9 w-9 transition', n <= stars ? 'fill-warn' : 'fill-line')}
                >
                  <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.5 7.7l5.9-.9z" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-6">
          <Button
            size="lg"
            full
            loading={busy}
            disabled={askPayment && !payment}
            onClick={submit}
          >
            {askPayment && !payment
              ? 'Informe como recebeu'
              : stars > 0
                ? 'Enviar e voltar a receber corridas'
                : 'Voltar a receber corridas'}
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
